import { useMemo } from 'react';
import { DOTS } from '@/constants/paginationConstants';

export interface Props {
  totalCount: number;
  pageSize: number;
  siblingCount?: number;
  currentPage: number;
}

function range(start: number, end: number): Array<number> {
  const length = end - start + 1;
  return Array.from({ length }, (_, idx) => idx + start);
}

/**
 * This hook expects count of total results, pageSize for page
 * siblingCount which defaults to 1 and selected page
 * siblingCount determines pageNumbers to be shown on either side of the currentPage
 * for eg: 1..456..10 this would mean if user is on page number 5 then with sibling count 1
 * 4 and 6 will show up in pagination range.
 * @returns Array with range of page numbers to be shown on a page
 */
export const usePagination = ({ totalCount, pageSize, siblingCount = 1, currentPage }: Props) => {
  const paginationRange = useMemo(() => {
    const totalPageCount = Math.min(Math.ceil(totalCount / pageSize), 400);

    // minimum elements for pagination including DOTS is siblingCount + firstPage + lastPage + currentPage + 2*DOTS
    const totalPageNumbers = siblingCount + 5;
    if (totalPageNumbers >= totalPageCount) {
      return range(1, totalPageCount);
    }
    const leftSiblingIndex = Math.max(currentPage - siblingCount, 1);
    const rightSiblingIndex = Math.min(currentPage + siblingCount, totalPageCount);

    // don't show dots if we have only one page between page start/end and the sibling index
    const shouldShowLeftDots = leftSiblingIndex > 3;
    const shouldShowRightDots = rightSiblingIndex < totalPageCount - 2;

    const firstPageIndex = 1;
    const lastPageIndex = totalPageCount;

    if (!shouldShowLeftDots && shouldShowRightDots) {
      const leftItemCount = 3 + 2 * siblingCount;
      const leftRange = range(1, leftItemCount);

      return [...leftRange, DOTS, totalPageCount];
    }

    if (shouldShowLeftDots && !shouldShowRightDots) {
      const rightItemCount = 3 + 2 * siblingCount;
      const rightRange = range(totalPageCount - rightItemCount + 1, totalPageCount);
      return [firstPageIndex, DOTS, ...rightRange];
    }

    if (shouldShowLeftDots && shouldShowRightDots) {
      const middleRange = range(leftSiblingIndex, rightSiblingIndex);
      return [firstPageIndex, DOTS, ...middleRange, DOTS, lastPageIndex];
    }
    return [];
  }, [totalCount, pageSize, siblingCount, currentPage]);

  return paginationRange;
};
